import * as pdfjsLib from 'pdfjs-dist/webpack';


import { jsPDF } from 'jspdf';

class PDFCompressor {
    /**
     * Compresses a PDF file by re-rendering it with reduced quality settings
     * @param {File|Blob|ArrayBuffer} pdfFile - The PDF file to compress
     * @param {Object} options - Compression options
     * @param {number} options.quality - Image quality (0.1 to 1.0, default: 0.5)
     * @param {number} options.imageResolution - Resolution for images in DPI (default: 150)
     * @param {boolean} options.grayscale - Convert images to grayscale (default: false)
     * @returns {Promise<Blob>} - A promise that resolves with the compressed PDF as a Blob
     */
    static async compressPDF(pdfFile, options = {}) {
        // Default options
        const compressionOptions = {
            quality: options.quality || 0.5,
            imageResolution: options.imageResolution || 150,
            grayscale: options.grayscale || false
        };

        // Convert input to ArrayBuffer if it's not already
        const pdfData = await this._getArrayBuffer(pdfFile);

        try {
            const loadingTask = pdfjsLib.getDocument({ data: pdfData });
            const pdf = await loadingTask.promise;

            // Get the first page to determine initial page size
            const firstPage = await pdf.getPage(1);
            const firstViewport = firstPage.getViewport({ scale: 1 });

            // Create a new PDF with jsPDF
            const newPdf = new jsPDF({
                orientation: firstViewport.width > firstViewport.height ? 'landscape' : 'portrait',
                unit: 'pt',
                format: [firstViewport.width, firstViewport.height]
            });

            // Process each page
            for (let i = 1; i <= pdf.numPages; i++) {
                // For pages after the first, we need to add a new page
                if (i > 1) {
                    const page = await pdf.getPage(i);
                    const viewport = page.getViewport({ scale: 1 });

                    // Add a new page with the correct dimensions
                    newPdf.addPage([viewport.width, viewport.height]);

                    // Create a canvas to render the page
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    canvas.height = viewport.height;
                    canvas.width = viewport.width;

                    // Render the PDF page to the canvas
                    await page.render({
                        canvasContext: context,
                        viewport: viewport
                    }).promise;

                    // Convert canvas to image and add to PDF
                    const imgData = canvas.toDataURL(
                        compressionOptions.grayscale ? 'image/jpeg' : 'image/png',
                        compressionOptions.quality
                    );

                    newPdf.addImage(
                        imgData,
                        compressionOptions.grayscale ? 'JPEG' : 'PNG',
                        0, 0,
                        viewport.width,
                        viewport.height,
                        `page-${i}`,
                        'FAST'
                    );
                } else {
                    // For the first page, use the canvas we already created
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    canvas.height = firstViewport.height;
                    canvas.width = firstViewport.width;

                    // Render the PDF page to the canvas
                    await firstPage.render({
                        canvasContext: context,
                        viewport: firstViewport
                    }).promise;

                    // Convert canvas to image and add to PDF
                    const imgData = canvas.toDataURL(
                        compressionOptions.grayscale ? 'image/jpeg' : 'image/png',
                        compressionOptions.quality
                    );

                    newPdf.addImage(
                        imgData,
                        compressionOptions.grayscale ? 'JPEG' : 'PNG',
                        0, 0,
                        firstViewport.width,
                        firstViewport.height,
                        `page-${i}`,
                        'FAST'
                    );
                }
            }

            // Generate the compressed PDF as a Blob
            const compressedPdfBlob = await newPdf.output('blob');
            return compressedPdfBlob;
        } catch (error) {
            console.error('Error compressing PDF:', error);
            throw error;
        }
    }

    /**
     * Gets the file size reduction percentage
     * @param {number} originalSize - Original file size in bytes
     * @param {number} compressedSize - Compressed file size in bytes
     * @returns {number} - Percentage of size reduction
     */
    static getReductionPercentage(originalSize, compressedSize) {
        return Math.round(((originalSize - compressedSize) / originalSize) * 100);
    }

    /**
     * Formats a file size in bytes to a human-readable string
     * @param {number} bytes - Size in bytes
     * @param {number} decimals - Number of decimal places
     * @returns {string} - Formatted file size
     */
    static formatFileSize(bytes, decimals = 2) {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];

        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    /**
     * Converts various input types to ArrayBuffer
     * @param {File|Blob|ArrayBuffer} input - The input file
     * @returns {Promise<ArrayBuffer>} - A promise that resolves with the array buffer
     * @private
     */
    static async _getArrayBuffer(input) {
        if (input instanceof ArrayBuffer) {
            return input;
        }

        if (input instanceof Blob || input instanceof File) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsArrayBuffer(input);
            });
        }

        throw new Error('Unsupported input type');
    }
}

// Export the PDFCompressor class for use in other modules
export default PDFCompressor;